import { MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { AutocompleteChangeReason, Box, LinearProgress } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Autocomplete } from "@mui/material";
import CardNFT from "./Card";
import { ExploreBaseProps, MusicNftWithPlayingState } from "./types";
import { AutocompleteOption, sortOptions } from "./types";
import { useContext, useEffect, useState } from "react";
import { FilterContainer, filterTextStyle, styles } from "./styles";
import { ResponsiveCenteredBox, SortContainer, useStyles } from "./styles";
import Mixpanel from "../../utils/mixpanel";
import { GridContainer } from "./styles";
import { GenresArray } from "../constants";
import { FractionalTransactionState } from "../../proto/api_pb";
import { UserDetailsContext } from "../../contexts";
import { FONT_FAMILY, TEXT_COLOR } from "../../theme";

export const ExploreBase: React.FC<ExploreBaseProps> = ({
  musicNFTs,
  isLoading,
  userProfile,
  isTrack,
  hideFeatured,
}) => {
  const [NFTs, setNFTs] = useState<Array<MusicNftWithPlayingState>>([]);
  const [sort, setSort] = useState(sortOptions[0]);
  const { allUsers, featuredUsers } = useContext(UserDetailsContext);
  const classes = useStyles();

  const isTrackFeatured = (track: MusicNftWithPlayingState): boolean => {
    return (
      featuredUsers.filter((user) => user.getUserId() === track.ownerId)
        .length > 0
    );
  };

  useEffect(() => {
    // randomize NFT order on load
    setNFTs(
      musicNFTs
        .map((value) => ({ ...value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map((n) => ({
          ...n,
          isPlaying: false,
        }))
    );
  }, [musicNFTs]);

  const handlePlayPause = (id: number) => {
    const currentSong = NFTs.find((nft) => nft.id === id);
    if (currentSong) {
      if (currentSong.isPlaying) {
        const updatedNfts = NFTs.map((n) =>
          n.id === id ? { ...n, isPlaying: false } : n
        );
        setNFTs(updatedNfts);
      } else {
        const updatedNfts = NFTs.map((n) => ({
          ...n,
          isPlaying: n.id === id,
        }));
        setNFTs(updatedNfts);
      }
    }
  };

  // Sort based on empty URL
  const customSort = (
    a: MusicNftWithPlayingState,
    b: MusicNftWithPlayingState
  ): number => {
    if (a.image === "" && b.image === "") {
      // If both have empty profile pic URLs, leave them in the current order
      return 0;
    } else if (a.image === "") {
      // Move objects with empty profile pic URLs to the end
      return 1;
    } else if (b.image === "") {
      return -1;
    } else {
      // Compare non-empty profile pic URLs
      return a.image.localeCompare(b.image);
    }
  };

  const stopAllSongs = () => {
    setNFTs(NFTs.map((n) => ({ ...n, isPlaying: false })));
  };

  const handleSort = (event: SelectChangeEvent<string>) => {
    Mixpanel.track("explore_sort", { sort: event.target.value });
    if (event.target.value === "None") {
      NFTs.sort(() => Math.random() - 0.5);
    } else if (event.target.value === "Recently Created") {
      NFTs.sort((a, b) => b.timestamp - a.timestamp);
    } else if (event.target.value === "Sort by Title") {
      NFTs.sort((a, b) => a.title.localeCompare(b.title));
    } else if (event.target.value === "Sort by Artist") {
      NFTs.sort((a, b) => a.artist.localeCompare(b.artist));
    }
    setSort(event.target.value);
  };

  const handleGenreChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteOption,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      Mixpanel.track("explore_genre_clear");
      const currentPlayingSongId = NFTs.find((n) => n.isPlaying)?.id;
      setNFTs(
        musicNFTs.map((n) => ({
          ...n,
          isPlaying: n.id === currentPlayingSongId,
        }))
      );
    } else {
      const genre = typeof value === "string" ? value : value?.item ?? "";
      const newArr = NFTs.filter((item) => {
        if (item.genres) {
          return item.genres.includes(genre);
        }
        return false;
      });
      Mixpanel.track("explore_genre_filter", { genre: genre });
      setNFTs([...newArr]);
    }
  };

  const options = GenresArray.map((option) => {
    const firstLetter = option[0].toUpperCase();
    return {
      firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
      item: option,
    };
  });

  const checkIfNftIsMinted = (n: MusicNftWithPlayingState) =>
    n.id && n.state === FractionalTransactionState.TRANSACTION_MINTED;

  return (
    <div style={{ width: "100%", padding: "0 2vw 0 2vw", marginBottom: 60 }}>
      <ResponsiveCenteredBox>
        <FilterContainer>
          <Autocomplete
            options={options.sort(
              (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
            )}
            freeSolo
            groupBy={(option) => option.firstLetter}
            getOptionLabel={(option: AutocompleteOption) =>
              typeof option === "string" ? option : option?.item ?? ""
            }
            sx={{
              width: 250,
              marginRight: 0.5,
              border: `1px solid ${TEXT_COLOR}`,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Genres"
                sx={filterTextStyle}
                classes={{ root: classes.customTextField }}
              />
            )}
            onChange={handleGenreChange}
          />
          <FilterAltIcon />
        </FilterContainer>
        <SortContainer>
          <Select
            style={{ border: "1px solid white", color: "white" }}
            value={sort}
            onChange={handleSort}
            sx={styles.sortSelect}
            placeholder="Sort"
            label=""
            variant="outlined"
          >
            {userProfile
              ? sortOptions.slice(0, -1).map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontFamily: FONT_FAMILY }}
                  >
                    {value}
                  </MenuItem>
                ))
              : sortOptions.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontFamily: FONT_FAMILY }}
                  >
                    {value}
                  </MenuItem>
                ))}
          </Select>
          <FilterListIcon />
        </SortContainer>
      </ResponsiveCenteredBox>
      {isLoading ? (
        <Box sx={styles.linearProgressContainer}>
          <LinearProgress sx={{ marginTop: "20vh" }} color="primary" />
        </Box>
      ) : (
        <>
          {NFTs.filter(isTrackFeatured).length > 0 &&
            !hideFeatured &&
            featuredUsers &&
            featuredUsers.length > 0 && (
              <Box sx={{ width: "100%", marginBottom: "20px" }}>
                <h2 style={styles.featuredHeading}>
                  {isTrack ? "Tracks" : "NFTs"} from our Featured Artists{" "}
                </h2>
                <GridContainer>
                  {NFTs.filter(isTrackFeatured).map((nft) => (
                    <CardNFT
                      key={nft.id}
                      nft={nft}
                      allUsers={allUsers}
                      stopAllSongs={stopAllSongs}
                      handlePlayPause={handlePlayPause}
                      isTrack={isTrack}
                    />
                  ))}
                </GridContainer>
              </Box>
            )}
          <Box sx={{ width: "100%", marginBottom: "20px" }}>
            <h2 style={styles.featuredHeading}>
              {isTrack ? "Tracks" : "NFTs"}
            </h2>
            <GridContainer>
              {NFTs.filter(checkIfNftIsMinted)
                .sort(customSort)
                .map((nft) => (
                  <CardNFT
                    key={nft.id}
                    nft={nft}
                    allUsers={allUsers}
                    stopAllSongs={stopAllSongs}
                    handlePlayPause={handlePlayPause}
                    isTrack={isTrack}
                  />
                ))}
            </GridContainer>
          </Box>
        </>
      )}
    </div>
  );
};
